import {
  CreateButton,
  DeleteButton,
  EditButton,
  ExportButton,
  FilterDropdown,
  getDefaultSortOrder,
  List,
  ShowButton,
  TextField,
  useSelect,
  useTable,
} from "@refinedev/antd";
import { CopyableEmailTag } from "components/fields/CopyableEmailTag";

import { Divider, Select, Space, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { IResourceComponentsProps, useExport } from "@refinedev/core";
import {
  IContact,
  IShipco,
  IShipcoGroup,
  IVessel,
} from "interfaces";
import React from "react";

export const ContactList: React.FC<IResourceComponentsProps> = () => {
  const { tableProps, sorters, tableQuery: { isLoading } } = useTable<IContact>({
    sorters: {
      initial: [
        {
          field: "id",
          order: "desc",
        },
      ]
    }
  });

  const { triggerExport, isLoading: isLoadingExport } = useExport<IContact>({
    mapData: (item) => {
      return {
        "Name": item?.name || "",
        "Email": item?.email || "",
        "Phone": item?.phone || "",
        "Shipco": item?.shipco?.registrationName || "",
        "Shipco Group": item?.shipcoGroup?.registrationName || "",
        "Vessel": item?.vessel?.name || "",
      };
    },
    unparseConfig: {
      header: true,
      columns: [
        "Name",
        "Email",
        "Phone",
        "Shipco",
        "Shipco Group",
        "Vessel",
      ],
    },
  });

  const { selectProps: shipcoSelectProps } = useSelect<IShipco>({
    resource: "shipco",
    optionLabel: "registrationName",
    optionValue: "registrationName",

    pagination: {
      mode: "server"
    }
  });

  const { selectProps: shipcoGroupSelectProps } = useSelect<IShipcoGroup>({
    resource: "shipco-group",
    optionLabel: "registrationName",
    optionValue: "registrationName",

    pagination: {
      mode: "server"
    }
  });

  const { selectProps: vesselSelectProps } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "name",
    optionValue: "name",

    pagination: {
      mode: "server"
    }
  });

  const columns: ColumnsType<IContact> = [
    {
      title: "Contact Name",
      dataIndex: "name",
      key: "name",
      width: 180,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("name", sorters),
      sorter: true,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 220,
      className: "wrap-text",
      render: (value) => <CopyableEmailTag value={value} />,
      defaultSortOrder: getDefaultSortOrder("email", sorters),
      sorter: true,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      width: 140,
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("phone", sorters),
      sorter: true,
    },
    {
      title: "Shipco",
      dataIndex: ["shipco", "registrationName"],
      key: "shipco.registrationName",
      width: 200,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select Shipco"
            {...shipcoSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Shipco Group",
      dataIndex: ["shipcoGroup", "registrationName"],
      key: "shipcoGroup.registrationName",
      width: 200,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select Shipco Group"
            {...shipcoGroupSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Vessel",
      dataIndex: ["vessel", "name"],
      key: "vessel.name",
      width: 180,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select Vessel"
            {...vesselSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <Space>
          <EditButton hideText size="small" recordItemId={record.id} />
          <ShowButton hideText size="small" recordItemId={record.id} />
          <DeleteButton hideText size="small" recordItemId={record.id} />
        </Space>
      ),
    },
  ];

  return (
    <List
      breadcrumb={false}
      headerProps={{
        // onBack: () => window.history.back(),
        subTitle: "active contacts",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        extra: (
          <React.Fragment>
            <Space>
              <CreateButton />
              <ExportButton onClick={triggerExport} loading={isLoadingExport} />
            </Space>
          </React.Fragment>
        ),
        ghost: true,
        footer: (
          <Divider plain>
            © {new Date().getFullYear()} e-Mariner NavSolutions
          </Divider>
        ),
      }}
    >
      <Table
        {...tableProps}
        columns={columns}
        rowKey="id"
        size="middle"
        scroll={{ x: 1200 }}
        sticky
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
        }}
      />
    </List>
  );
};
